import { ReactRenderer } from '@tiptap/react';
import type {
    SuggestionKeyDownProps,
    SuggestionOptions,
    SuggestionProps,
} from '@tiptap/suggestion';
import Fuse from 'fuse.js';
import { translateBlock } from 'i18n';
import type { DataCatalogField } from 'modules/datacatalog/types';

import FieldList from './FieldList';
import type { FieldListActions } from './types';

const filterFields = (
    fuse: Fuse<DataCatalogField>,
    fields: DataCatalogField[],
    query: string,
): DataCatalogField[] => {
    if (query === '') {
        return fields.slice(0, 8);
    }
    return fuse
        .search(query)
        .slice(0, 8)
        .map((o) => o.item);
};

export const FieldSuggestion = (
    fields: DataCatalogField[] | undefined,
): Partial<SuggestionOptions<DataCatalogField>> => {
    const fuse = new Fuse(fields ?? [], {
        keys: [
            { name: 'field', getFn: (f) => translateBlock(f.field) },
            { name: 'table', getFn: (f) => translateBlock(f.table) },
        ],
        threshold: 0.5,
        isCaseSensitive: false,
        ignoreLocation: true,
    });

    return {
        char: '#',
        allowSpaces: true,
        items: ({ query }) => filterFields(fuse, fields ?? [], query),
        render: () => {
            let component: ReactRenderer<FieldListActions>;

            return {
                onStart: (props: SuggestionProps) => {
                    component = new ReactRenderer(FieldList, {
                        props,
                        editor: props.editor,
                    });
                },

                onUpdate(props: SuggestionProps) {
                    component.updateProps(props);
                },

                onKeyDown(props: SuggestionKeyDownProps) {
                    if (props.event.key === 'Escape') {
                        component.destroy();
                        return true;
                    }
                    return component.ref?.onKeyDown(props) ?? false;
                },

                onExit() {
                    component.destroy();
                },
            };
        },
    };
};
